import { Product, Currency } from '../data/productModels';

export type { Currency };

/**
 * Unified helper to get price in cents.
 * Handles capitalization safety and null checks.
 */
export function getUnitPriceCents(product: Product | undefined, currency: string): number | null {
  if (!product || !product.prices) return null;

  const cur = String(currency || '').toUpperCase() as Currency;
  const entry = product.prices[cur];

  if (entry && typeof entry.priceCents === 'number') {
    return entry.priceCents;
  }

  return null;
}

/**
 * Helper to determine physical inventory status.
 * Independent of currency/price.
 */
export function getInventoryStatus(product: Product | undefined) {
  if (!product) return { available: 0, isOutOfStock: true };

  const stock = product.stockOnHand ?? 0;
  const reserved = product.reservedQty ?? 0;
  const available = stock - reserved;

  // Status check + Math check
  const isOutOfStock = product.status !== 'active' || available <= 0;

  return { available, isOutOfStock };
}

/**
 * Determines if a product can be purchased in the given currency.
 * Rules:
 * 1. Product is active and has stock (Available > 0)
 * 2. Product has a valid price > 0 in the requested currency
 */
export function isPurchasable(product: Product | undefined, currency: string): boolean {
  const { isOutOfStock } = getInventoryStatus(product);
  if (isOutOfStock) return false;

  const price = getUnitPriceCents(product, currency);
  return price !== null && price > 0;
}

// Deprecated alias to maintain backward compatibility if used elsewhere,
// but code should migrate to getUnitPriceCents
export const getProductPrice = getUnitPriceCents;
